import { useEffect, useState } from 'react';

import { Call, Device } from '@twilio/voice-sdk';

import { TWILIO_SIGNALING_ENDPOINT } from '../envs';
import { CallWithDuration } from '../types';
import { applyDeviceSettings } from '../utils';
import { settingsService } from '../services';

const useTwilioCalls = (device?: Device) => {
  const [calls, setCalls] = useState<CallWithDuration[]>([]);
  const [currentCall, setCurrentCall] = useState<Call>();

  const forwardCall = async(call: Call) => {
    let callDevice;
    if (TWILIO_SIGNALING_ENDPOINT) {
      const deviceParams = {
        chunderw: TWILIO_SIGNALING_ENDPOINT,
        eventgw: TWILIO_SIGNALING_ENDPOINT,
      };
      callDevice = new Device(device?.token, deviceParams);
    } else {
      callDevice = new Device(device?.token);
    }

    const forwardedCall = await callDevice.connect({ connectToken: call.connectToken }) as CallWithDuration;

    // Set durationInitial for the forwarded call
    forwardedCall.durationInitial = Date.now();

    forwardedCall.on('disconnect', () => callDevice.destroy());
    settingsService.on('change', () => applyDeviceSettings(callDevice));

    applyDeviceSettings(callDevice);

    // Replace existing call with forwarded and accepted one
    setCalls(prevCalls => {
      const callSid = call.parameters?.CallSid;
      const idx = prevCalls.findIndex(
        (item) => item.parameters?.CallSid === callSid,
      );
      if (idx === -1) return [forwardedCall, ...prevCalls];
      const newCalls = [...prevCalls];
      newCalls[idx] = forwardedCall;
      return newCalls;
    });

    setCurrentCall(forwardedCall);

    return call;
  };

  useEffect(() => {
    if (!device) {
      return;
    }

    const onIncomingCall = async(call: CallWithDuration) => {
      const durationInitial = Date.now();
      Object.assign(call, { durationInitial });
      setCalls(prevCalls => {
        // Prevent duplicates
        if (prevCalls.some(c => c.parameters?.CallSid === call.parameters?.CallSid)) {
          return prevCalls;
        }
        return [call, ...prevCalls];
      });
    };

    const onCallConnect = (call: CallWithDuration) => {
      // Set durationInitial if not already set (for calls not added by makeCall)
      if (!call.durationInitial) {
        call.durationInitial = Date.now();
      }
      setCalls(prevCalls => {
        // Prevent duplicates - makeCall already adds outgoing calls
        if (prevCalls.some(c => c.parameters?.CallSid === call.parameters?.CallSid)) {
          return prevCalls;
        }
        return [call, ...prevCalls];
      });
    };

    device.on('incoming', onIncomingCall);
    device.on('connect', onCallConnect);

    return () => {
      device.off('incoming', onIncomingCall);
      device.off('connect', onCallConnect);
    };
  }, [device]);

  const makeCall = async(to: string) => {
    if (!device) {
      console.error('Device not initialized in useTwilioCalls.makeCall');
      return null;
    }

    console.info('Making call with device:', device);
    const call: Call = await device.connect({ params: { To: to } });
    console.info('Call created:', call);

    const durationInitial = Date.now();
    Object.assign(call, { durationInitial });

    setCalls(prevCalls => [call as CallWithDuration, ...prevCalls]);
    setCurrentCall(call);

    call.on('disconnect', () => {
      const durationFinal = Date.now();
      Object.assign(call, { durationFinal });
      setCalls(prevCalls => {
        const idx = prevCalls.findIndex(c => c.parameters?.CallSid === call.parameters?.CallSid);
        if (idx === -1) return [call as CallWithDuration, ...prevCalls];
        const newCalls = [...prevCalls];
        newCalls[idx] = call as CallWithDuration;
        return newCalls;
      });
    });

    return call;
  };

  return {
    calls,
    currentCall,
    makeCall,
    setCalls,
    forwardCall,
    setCurrentCall,
  };
};

export default useTwilioCalls;
